import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { changePassword, loginByRegisterToken, loginByResetPasswordToken, registerUser, type LoginResultDTO } from '../services/AuthService';
import { useAuth } from '../security/AuthContext';

const SETUP_PASSWORD = 'setup_password';
const RESET_PASSWORD = 'reset_password';

export function Signup() {
  const { login } = useAuth();
  const [error, setError] = useState(null);
  const [emailDisabled, setEmailDisabled] = useState(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const [workflow, setWorkflow] = useState('');

  const showError = (text, err) => {
    const errorMessage = text + ' ' + err.response?.data?.message || 'An error had occurred. Please try again.';
    setError(errorMessage);
    console.error(errorMessage);
  }

  const applyLoginResult = (data:LoginResultDTO) => {
    localStorage.setItem('token', data.jwt);
    localStorage.setItem('refreshToken', data.refreshToken);
    login(data.user);
  }

  const navigateOriginal = () => {
    setTimeout(() => {
      const origUrl = localStorage.getItem("originalUrl")
      var url = "/";
      if (origUrl) {
        const urlObj = new URL(origUrl);
        urlObj.searchParams.delete('registerToken');
        const fullUrl = urlObj.toString();
        url = fullUrl.substring(fullUrl.replace("//", "").indexOf("/") + 2)
      }
      navigate(url);
    }, 100);
  }

  const initPasswordWorkflow = (w:string) => {
    const token = localStorage.getItem(w == SETUP_PASSWORD ? 'registerToken' : 'resetPasswordToken');
    if (token) {
      (w == SETUP_PASSWORD ? loginByRegisterToken({ token }) : loginByResetPasswordToken({ token }))
        .then((data:LoginResultDTO) => {
          if (data.success) {
            applyLoginResult(data);
            if (w == SETUP_PASSWORD && data.user.status == 'Confirmed') {
              navigateOriginal();
              return;
            }
            firstNameRef.current.value = data.user.firstName;
            lastNameRef.current.value = data.user.lastName;
            emailRef.current.value = data.user.email;
            emailRef.current.disabled = true;
            setEmailDisabled(true);
          } else {
            setError(data.details);
          }
        })
      .catch((err) => {
        showError('Login by token failed:', err)
      });
    }
  };


  useEffect(() => {
    setEmailDisabled(false);
    const urlParams = new URLSearchParams(window.location.search);
    const w = urlParams.get('workflow');
    if (w == SETUP_PASSWORD || w == RESET_PASSWORD) {
      setWorkflow(w);
      initPasswordWorkflow(w);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const firstName = e.target.firstName.value;
    const lastName = e.target.lastName.value;
    const email = e.target.email.value;
    const password = e.target.password.value;
    const confirmPassword = e.target.confirm_password.value;

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (workflow == SETUP_PASSWORD) {
      try {
        const data = await registerUser({
          firstName: firstName,
          lastName: lastName,
          email: email,
          password: password, 
          token: localStorage.getItem('registerToken')
        });

        applyLoginResult(data);
        navigateOriginal()

      } catch (err) {
        showError('Error during password change', err);
      }
    }

    if (workflow == RESET_PASSWORD) {
      try {
        const data = await changePassword({
          password: password, 
          token: localStorage.getItem('resetPasswordToken')
        });

        applyLoginResult(data);
        navigateOriginal()

      } catch (err) {
        showError('Error during registration', err);
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 ">
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md m-10 relative">
        <h2 className="text-4xl font-bold text-center text-gray-800 mb-6">
          {workflow == SETUP_PASSWORD ? 'Complete Registration' : 'Change Password'}
        </h2>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
              First Name
            </label>
            <input
              ref={firstNameRef}
              id="firstName"
              name="firstName"
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-400 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="First Name"
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
              Last Name
            </label>
            <input
              ref={lastNameRef}
              id="lastName"
              name="lastName"
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-400 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Last name"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email Address
            </label>
            <input
              ref={emailRef}
              id="email"
              name="email"
              type="email"
              required
              disabled={emailDisabled}
              className={"mt-1 block w-full px-3 py-2 border border-gray-400 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" + (emailDisabled?'text-gray-500 opacity-50':'')}
              placeholder="you@example.com"
            />
          </div>
          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                className="mt-1 block w-full pr-10 px-3 py-2 border border-gray-400 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-2 flex items-center text-gray-600 hover:text-gray-800"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
              </button>
            </div>
          </div>
          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <div className="relative">
              <input
                id="confirm_password"
                name="confirm_password"
                type={showConfirmPassword ? 'text' : 'password'}
                required
                className="mt-1 block w-full pr-10 px-3 py-2 border border-gray-400 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                className="absolute inset-y-0 right-2 flex items-center text-gray-600 hover:text-gray-800"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                <i className={showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
              </button>
            </div>
          </div>
          <div>
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-xl font-medium text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
            >
              {workflow == SETUP_PASSWORD ? 'Complete' : 'Save'}
            </button>
          </div>
        </form>
        <p className="mt-4 text-center text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
            Login
          </Link>
        </p>
        <div className="bottom-2 w-full text-center py-4 ">
          <p className="text-base">© 2025 SendFalcon. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
